import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Stack from '@mui/material/Stack';

import Error from 'components/error';
import styled from 'shared/utils/styled';

import { pdfViewerApi } from '../../api/pdfViewerApi';
import { PATH_TO_VIEWER } from '../../lib/common';

const Container = styled('div')(() => ({
  position: 'relative',
  height: '100%',
}));

const StyledIframe = styled('iframe')(() => ({
  display: 'block',
  width: '100%',
  height: '100%',

  minHeight: 500,

  border: 0,
  outline: 0,
}));

type TPDFViewerProps = {
  url: string;
};

export type PDFViewerRef = {
  disabled: boolean;
  download: (filename?: string) => void;
  print: () => void;
};

const PDFViewer = forwardRef<PDFViewerRef, TPDFViewerProps>(function PDFViewer(
  props,
  ref
) {
  const { url } = props;

  const pdfViewerRef = useRef<HTMLIFrameElement>(null);

  const [ready, setReady] = useState(false);

  const {
    data: objectURL,
    isLoading,
    isFetching,
    isError,
  } = pdfViewerApi.useGetPDFQuery(
    { pathname: url },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const handleDownloadPDF = useCallback(
    (filename: string) => {
      if (!objectURL) return;

      const fileLink = document.createElement('a');
      fileLink.href = objectURL;
      fileLink.download = filename;
      fileLink.click();
    },
    [objectURL]
  );

  useImperativeHandle(
    ref,
    () => {
      const PDFViewerApplication = (pdfViewerRef.current?.contentWindow as any)
        ?.PDFViewerApplication;

      return {
        disabled: !ready,
        print: () => PDFViewerApplication?.triggerPrinting(),
        download: (filename) => {
          filename
            ? handleDownloadPDF(filename)
            : PDFViewerApplication?.download();
        },
      };
    },
    [ready, handleDownloadPDF]
  );

  useEffect(() => {
    if (pdfViewerRef.current && objectURL && ready) {
      const PDFViewerApplication = (pdfViewerRef.current?.contentWindow as any)
        ?.PDFViewerApplication;

      PDFViewerApplication.open({ url: objectURL });
    }
  }, [objectURL, ready]);

  if (isError)
    return (
      <Stack alignItems="center" justifyContent="center">
        <Error />
      </Stack>
    );

  const pending = isLoading || isFetching;

  return (
    <Container>
      <StyledIframe
        src={PATH_TO_VIEWER}
        ref={pdfViewerRef}
        sx={{
          transition: 'filter .15s linear',
          ...(pending && {
            filter: 'blur(3px)',
          }),
        }}
        onLoad={() => setReady(true)}
      />
    </Container>
  );
});

export default PDFViewer;
